import { StickyNavApiEntry } from '@interfaces/models/sticky-nav';
import { WOMEN_UNIVERSE, MEN_UNIVERSE, SESSION_KEY } from '@constants/user-shopping-preferences';
import { VisitorType } from '@enums/visitor-identity';
import { isCatalogOrCampaignPage, isHomePage } from '@helpers/routing';
import Environment from '@config/environment';

export interface StickyNavEvaluationParams {
  isMobile: boolean | null;
  visitorType?: VisitorType;
  isWebView: boolean;
  pathname: string;
  hasFromStickyNav: boolean;
}

export const shouldEvaluateStickyNavFF = (params: StickyNavEvaluationParams): boolean => {
  const { isMobile, visitorType, isWebView, pathname, hasFromStickyNav } = params;

  if (isMobile !== true) {
    return false;
  }

  const isUserLegit = visitorType === VisitorType.NormalUser;

  if (!isUserLegit || isWebView) {
    return false;
  }

  const isHomepage = isHomePage(pathname);
  const isCatalogOrCampaign = isCatalogOrCampaignPage(pathname);

  if (!isHomepage && !isCatalogOrCampaign) {
    return false;
  }

  if (!isHomepage && !hasFromStickyNav) {
    return false;
  }

  return true;
};

export const extractCategoryParentIdFromHash = (url: string): string | null => {
  try {
    const hash = url.split('#')[1];
    if (!hash) {
      return null;
    }

    // Matches categoryParent=...%23(\d+) format (encoded #)
    const categoryParentMatch = hash.match(/categoryParent=[^_]*%23(\d+)/);
    if (categoryParentMatch) {
      return categoryParentMatch[1];
    }

    // Matches categoryParent=...#(\d+) format (unencoded #)
    const categoryParentMatch2 = hash.match(/categoryParent=[^_]*#(\d+)/);
    if (categoryParentMatch2) {
      return categoryParentMatch2[1];
    }

    return null;
  } catch {
    return null;
  }
};

export const normalizePath = (path: string): string => {
  const baseOrigin =
    typeof window !== 'undefined' && window.location?.origin ? window.location.origin : Environment.baseHost;

  if (baseOrigin) {
    try {
      const fullUrl = path.startsWith('http') ? path : `${baseOrigin}${path}`;
      const { pathname } = new URL(fullUrl);
      const normalized = pathname.toLowerCase().replace(/\/$/, '');
      return normalized || '/';
    } catch {
      // Fall through to manual parsing if URL constructor fails
    }
  }

  const pathWithoutQuery = path.split('?')[0];
  const pathWithoutHash = pathWithoutQuery.split('#')[0];
  const normalized = pathWithoutHash.toLowerCase().replace(/\/$/, '');
  return normalized || '/';
};

// Gets the session universe preference from sessionStorage.
export const getSessionUniverse = (): number | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const sessionPrefs = sessionStorage.getItem(SESSION_KEY);
    if (sessionPrefs) {
      const parsed = JSON.parse(sessionPrefs);
      return parsed?.universe;
    }
  } catch {
    return undefined;
  }
  return undefined;
};

export interface GetCurrentUniverseParams {
  universeFromQuery?: number;
  sessionUniverse?: number;
  persistedUniverse?: number;
}

export const getCurrentUniverse = ({
  universeFromQuery,
  sessionUniverse,
  persistedUniverse,
}: GetCurrentUniverseParams): number => {
  // Check URL query param before preferences (for immediate updates when universe changes, shopping preference not updated yet)
  // URL query param is first because when user switches preference, SSR still has the old value,
  // so the URL (reflecting immediate user action) should take precedence over stale SSR data
  if (universeFromQuery !== undefined && !isNaN(universeFromQuery)) {
    return universeFromQuery === MEN_UNIVERSE ? MEN_UNIVERSE : WOMEN_UNIVERSE;
  }

  // Use session preference (from sessionStorage) first, then fallback to persisted (localStorage/backend)
  const universe = sessionUniverse || persistedUniverse || WOMEN_UNIVERSE;
  return universe === WOMEN_UNIVERSE || universe === MEN_UNIVERSE ? universe : WOMEN_UNIVERSE;
};

export interface GetActiveEntryPointParams {
  routerPathname: string;
  routerAsPath: string;
  entryPoints: StickyNavApiEntry[];
  currentUniverse: number;
}

export const getActiveEntryPoint = ({
  routerPathname,
  routerAsPath,
  entryPoints,
  currentUniverse,
}: GetActiveEntryPointParams): string | null => {
  const rawPathForPath = routerAsPath;
  const rawPathForQueryAndHash = routerAsPath;

  const pathWithoutQuery = rawPathForPath.split('?')[0];
  const pathWithoutHash = pathWithoutQuery.split('#')[0];
  const currentPath = pathWithoutHash.toLowerCase();

  const isHomepage = isHomePage(routerPathname, routerAsPath);

  if (isHomepage) {
    const womenEntry = entryPoints.find((entry) => entry.mnemonic === 'women');
    const menEntry = entryPoints.find((entry) => entry.mnemonic === 'men');

    if (womenEntry && !menEntry) {
      return womenEntry.id;
    }

    if (menEntry && !womenEntry) {
      return menEntry.id;
    }

    const preferredEntry = currentUniverse === WOMEN_UNIVERSE ? womenEntry : menEntry;
    return preferredEntry?.id ?? null;
  }

  const stickyCategoryIdFromQuery = (() => {
    try {
      const [, queryString] = rawPathForQueryAndHash.split('?');
      if (queryString) {
        const search = queryString.split('#')[0];
        const params = new URLSearchParams(search);
        return params.get('stickyCategoryId');
      }
    } catch {
      return null;
    }
    return null;
  })();

  const currentPageCategoryParentId =
    stickyCategoryIdFromQuery || extractCategoryParentIdFromHash(rawPathForQueryAndHash);

  if (currentPageCategoryParentId) {
    const categoryMatch = entryPoints.find((entry) => entry.destination?.categoryId === currentPageCategoryParentId);
    if (categoryMatch) {
      return categoryMatch.id;
    }
  }

  const normalizedCurrentPath = normalizePath(currentPath);

  const matchingEntryPoint = entryPoints.reduce<StickyNavApiEntry | null>((acc, entry) => {
    if (!entry.destination?.universalLink) {
      return acc;
    }
    const normalizedEntryUrl = normalizePath(entry.destination.universalLink);
    if (normalizedCurrentPath === normalizedEntryUrl || normalizedCurrentPath.startsWith(`${normalizedEntryUrl}/`)) {
      if (!acc || normalizedEntryUrl.length > normalizePath(acc.destination?.universalLink || '').length) {
        return entry;
      }
    }
    return acc;
  }, null);

  return matchingEntryPoint?.id ?? null;
};

export interface ShouldHideStickyNavParams {
  isWiderThanMd: boolean;
  hasWindowSizeBeenSet: boolean;
}

export const shouldHideStickyNav = ({ isWiderThanMd, hasWindowSizeBeenSet }: ShouldHideStickyNavParams): boolean => {
  if (hasWindowSizeBeenSet) {
    return isWiderThanMd;
  }
  return false;
};

export const getEffectiveAsPath = (currentUrl: string, fallbackAsPath: string): string => {
  if (!currentUrl) {
    return fallbackAsPath;
  }

  try {
    if (currentUrl.startsWith('http')) {
      const url = new URL(currentUrl);
      return url.pathname + url.search + url.hash;
    }
    return currentUrl;
  } catch {
    return fallbackAsPath;
  }
};

export const getStickyEntryPoints = (stickyNavData: StickyNavApiEntry[] | null | undefined): StickyNavApiEntry[] => {
  const data = stickyNavData ?? [];

  return data
    .filter((entry) => entry.destination?.universalLink)
    .map((entry) => {
      const categoryId = entry.destination?.categoryId || '';

      if (!entry.destination?.universalLink || entry.destination.universalLink === '/') {
        return {
          ...entry,
          destination: {
            ...entry.destination,
            categoryId,
          },
        };
      }

      try {
        const baseOrigin =
          typeof window !== 'undefined' && window.location?.origin ? window.location.origin : Environment.baseHost;

        if (!baseOrigin) {
          return {
            ...entry,
            destination: {
              ...entry.destination,
              categoryId,
            },
          };
        }

        const baseUrl = `${baseOrigin}${entry.destination.universalLink}`;
        const url = new URL(baseUrl);
        url.searchParams.set('fromStickyNav', '1');
        if (categoryId) {
          url.searchParams.set('stickyCategoryId', categoryId);
        }

        const pathname = url.pathname.endsWith('/') && url.pathname !== '/' ? url.pathname.slice(0, -1) : url.pathname;
        const queryString = url.searchParams.toString();
        const hash = url.hash;
        const transformedLink = queryString ? `${pathname}?${queryString}${hash}` : `${pathname}${hash}`;

        return {
          ...entry,
          destination: {
            ...entry.destination,
            universalLink: transformedLink,
            categoryId,
          },
        };
      } catch {
        return {
          ...entry,
          destination: {
            ...entry.destination,
            categoryId,
          },
        };
      }
    });
};
